import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const SUCCESS_MESSAGE_DURATION = 2500;

class SocksMultibuySelector extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-socks-form]');
    this.tierInputs = Array.from(this.querySelectorAll('[data-socks-tier]'));
    this.tierCards = Array.from(this.querySelectorAll('[data-socks-tier-card]'));
    this.choicesPanel = this.querySelector('[data-socks-choices]');
    this.choices = Array.from(this.querySelectorAll('[data-socks-choice]'));
    this.selects = Array.from(this.querySelectorAll('[data-socks-select]'));
    this.button = this.querySelector('[data-socks-submit]');
    this.buttonText = this.querySelector('[data-socks-button-text]');
    this.status = this.querySelector('[data-socks-status]');
    this.stock = this.querySelector('[data-socks-stock]');
    this.stockText = this.querySelector('[data-socks-stock-text]');

    if (
      !this.form ||
      this.tierInputs.length !== 3 ||
      !this.choicesPanel ||
      !this.choices.length ||
      !this.button ||
      !this.buttonText
    ) {
      return;
    }

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tierInputs.forEach((input) => input.addEventListener('change', this.handleTierChange, { signal }));
    this.selects.forEach((select) => select.addEventListener('change', this.handleSelectionChange, { signal }));
    this.form.addEventListener('submit', this.handleSubmit, { signal });

    const selectedInput =
      this.tierInputs.find((input) => input.checked) ||
      this.tierInputs.find((input) => input.value === this.dataset.defaultTier) ||
      this.tierInputs[0];

    selectedInput.checked = true;
    this.activateTier(selectedInput);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.resetTimeout) clearTimeout(this.resetTimeout);
  }

  handleTierChange = (event) => {
    this.activateTier(event.currentTarget);
  };

  handleSelectionChange = (event) => {
    const select = event.currentTarget;
    const choice = select.closest('[data-socks-choice]');
    const image = choice?.querySelector('[data-socks-image] img');
    const selected = select.selectedOptions[0];

    if (image && selected?.dataset.image) {
      image.removeAttribute('srcset');
      image.src = selected.dataset.image;
    }

    if (image && selected?.textContent) {
      image.alt = selected.textContent.trim();
    }

    this.updateOffer();
  };

  handleSubmit = async (event) => {
    event.preventDefault();
    if (this.button.disabled) return;

    const selections = this.getActiveSelects().map((select) => select.selectedOptions[0]);
    if (!selections.length || selections.some((option) => !option || option.dataset.available !== 'true')) {
      this.updateOffer();
      return;
    }

    const quantities = new Map();
    selections.forEach((option) => quantities.set(option.value, (quantities.get(option.value) || 0) + 1));

    const items = Array.from(quantities, ([id, quantity]) => ({ id: Number(id), quantity }));
    const lines = items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity }));
    const itemCount = selections.length;

    this.setLoading(true);
    this.setStatus('');

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines,
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items,
          sections: this.getCartSectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || this.dataset.errorLabel);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      if (!cartResponse.ok || cart.status) {
        throw new Error(cart.description || cart.message || this.dataset.errorLabel);
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          sections: result.sections,
          items: cart.items,
          itemCount,
          source: 'socks-multibuy-selector',
          sourceId: this.id,
          productId: this.dataset.productId,
          didError: false,
        },
      });

      this.buttonText.textContent = this.dataset.addedLabel;
      this.setStatus(this.dataset.addedLabel);
      this.resetTimeout = setTimeout(() => {
        this.buttonText.textContent = this.dataset.buttonLabel;
        this.setStatus('');
      }, SUCCESS_MESSAGE_DURATION);
    } catch (error) {
      deferred.reject(error);
      const message = error instanceof Error && error.message ? error.message : this.dataset.errorLabel;

      this.dispatchEvent(
        new CartErrorEvent({
          error: message,
          code: 'INVALID',
        })
      );

      this.setStatus(message);
      this.buttonText.textContent = this.dataset.buttonLabel;
    } finally {
      this.setLoading(false);
    }
  };

  activateTier(input) {
    const selectedCard = input.closest('[data-socks-tier-card]');
    if (!selectedCard) return;

    this.tierCards.forEach((card) => {
      const isSelected = card === selectedCard;
      card.dataset.selected = String(isSelected);
      card.setAttribute('aria-current', String(isSelected));
    });

    const quantity = Math.min(Number(selectedCard.dataset.tierQuantity || 1), this.choices.length);
    this.activeQuantity = quantity;
    this.activeDiscount = Number(selectedCard.dataset.tierDiscount || 0);

    this.choices.forEach((choice, index) => {
      const active = index < quantity;
      choice.hidden = !active;
      const select = choice.querySelector('[data-socks-select]');
      if (select) select.disabled = !active;
    });

    this.choicesPanel.hidden = false;
    selectedCard.append(this.choicesPanel);
    this.choicesPanel.dataset.choiceCount = String(quantity);
    this.updateOffer();
  }

  updateOffer() {
    this.updateTierPrices();

    const activeOptions = this.getActiveSelects().map((select) => select.selectedOptions[0]);
    const available =
      activeOptions.length === this.activeQuantity &&
      activeOptions.every((option) => option?.dataset.available === 'true');

    this.button.disabled = !available;
    this.stock?.setAttribute('data-available', String(available));

    if (this.stockText) {
      this.stockText.textContent = available ? this.dataset.availableLabel : this.dataset.unavailableLabel;
    }

    this.refreshCurrencyConverter();
  }

  updateTierPrices() {
    this.tierCards.forEach((card) => {
      const quantity = Math.min(Number(card.dataset.tierQuantity || 1), this.selects.length);
      const discount = Number(card.dataset.tierDiscount || 0);
      const subtotal = this.selects.slice(0, quantity).reduce((sum, select) => {
        return sum + Number(select.selectedOptions[0]?.dataset.price || 0);
      }, 0);
      const discountedTotal = Math.round((subtotal * (100 - discount)) / 100);
      const savings = subtotal - discountedTotal;

      this.updateMoney(card.querySelector('[data-tier-price]'), discountedTotal);
      this.updateMoney(card.querySelector('[data-tier-compare]'), subtotal);

      const savingsSlot = card.querySelector('[data-tier-savings] .money');
      if (savingsSlot) savingsSlot.textContent = formatMoney(savings, this.dataset.moneyFormat, this.dataset.currency);
    });
  }

  updateMoney(slot, amount) {
    if (!slot) return;

    const money = document.createElement('span');
    money.className = 'money';
    money.textContent = formatMoney(amount, this.dataset.moneyFormat, this.dataset.currency);
    slot.replaceChildren(money);
  }

  getActiveSelects() {
    return this.selects.slice(0, this.activeQuantity || 1);
  }

  refreshCurrencyConverter() {
    const refresh = () => requestAnimationFrame(() => window.bucksCC?.reConvert?.());
    if (typeof window.bucksCC?.reConvert === 'function') refresh();
    else window.addEventListener('BUCKSCC-LOADED', refresh, { once: true });
  }

  getCartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);
  }

  setLoading(isLoading) {
    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', String(isLoading));

    if (isLoading) {
      this.buttonText.textContent = this.dataset.addingLabel;
    } else {
      this.updateOffer();
    }
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message;
  }
}

if (!customElements.get('socks-multibuy-selector')) {
  customElements.define('socks-multibuy-selector', SocksMultibuySelector);
}
